/**
 * Physical constants and material data, all SI unless the name says otherwise.
 *
 * Everything here is a real measured quantity. The one place this model uses a
 * fitted number rather than a measured one is `ETCH_CALIBRATION` in kinetics.ts,
 * which is called out there explicitly.
 */

export const KB = 1.380649e-23 // J/K
export const ELEM_CHARGE = 1.602176634e-19 // C
export const AMU = 1.66053906660e-27 // kg
export const R_GAS = 8.314462618 // J/(mol K)
export const AVOGADRO = 6.02214076e23 // 1/mol

/** 1 mTorr in pascals. */
export const MTORR = 0.1333223684
export const ATM = 101325 // Pa

/** Convert a pressure in mTorr to Pa. */
export const mtorrToPa = (mtorr: number) => mtorr * MTORR

/** Ideal-gas number density [m^-3] from pressure [Pa] and temperature [K]. */
export const numberDensity = (pressurePa: number, tempK: number) => pressurePa / (KB * tempK)

/** Mean thermal speed sqrt(8kT/pi m) [m/s] for a particle of mass massAmu. */
export function thermalSpeed(tempK: number, massAmu: number): number {
  return Math.sqrt((8 * KB * tempK) / (Math.PI * massAmu * AMU))
}

/**
 * Binary diffusion coefficient [m^2/s] via the standard Chapman-Enskog
 * temperature-and-pressure scaling D ~ T^1.75 / p, referenced to a measured
 * value at STP. Radicals in a low-pressure chamber diffuse fast: at 20 mTorr
 * this returns of order 1 m^2/s, which is why transport equilibrates in
 * milliseconds while the film takes minutes to clear.
 */
export function diffusivity(pressurePa: number, tempK: number, dRefStp = 1.5e-5): number {
  return dRefStp * Math.pow(tempK / 300, 1.75) * (ATM / Math.max(pressurePa, 1e-6))
}

/** Species masses [amu]. */
export const MASS = {
  CF4: 88.0,
  F: 19.0,
  Ar: 39.95,
  CF3_ION: 69.0,
} as const

/** Film and substrate atomic densities [m^-3]. */
export const FILM_DENSITY = {
  Si: 5.0e28,
  SiO2: 2.2e28,
  photoresist: 1.0e28,
} as const

/** Silicon + 4F -> SiF4: four fluorine atoms consumed per silicon atom removed. */
export const F_PER_SI = 4
